import { useEffect, useState } from 'react';
import type { Canal } from '../../api/ops/canales';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Campo } from '../../componentes/Campo';
import { Chip, type EstadoChip } from '../../componentes/Chip';
import { Deshacer } from '../../componentes/Deshacer';
import { Icono } from '../../componentes/Icono';
import { PanelLateral } from '../../componentes/PanelLateral';
import { useEnvioRetrasado } from '../../logica/envioRetrasado';
import { avisoBreveDeError } from '../../logica/estados';
import { diaYHora } from '../../logica/fechasCortas';
import { usePermisos } from '../../permisos/permisos';
import type { PropsApartado } from './Canales';
import { AYUDA_DOMINIOS_CHAT, dominiosDelChat, ESTADO_ASISTENTE, LINEA_CHAT_WEB, nombreDe, origenesDe, TIPO } from './reglas';

/**
 * Ficha mínima del chat de la web (11.1, G715): dominios desde los que se abre (`allowed_origins`, editables con
 * `updateChannel` y su `If-Match`, con «Deshacer» 5 s), cuándo empezó la última conversación (`last_conversation_at`) y
 * el estado del Asistente (`getAssistant`, solo si quien mira tiene `asistente.configuracion.editar`; si no, nada). Sin
 * salud, sin incidencias, sin «Comprobar ahora» y sin «Desconectar»: la API responde `422` a las dos con `web_chat`.
 */
export function FichaChatWeb(props: PropsApartado & { canal: Canal; onCerrar: () => void }) {
    const { ops, negocio, movil, soloLectura, recargarDatos, canal, onCerrar } = props;
    const { puede } = usePermisos();
    const gestiona = puede('canales.canales.gestionar');
    const verAsistente = puede('asistente.configuracion.editar');
    const avisar = useAvisoBreve();
    const retraso = useEnvioRetrasado();
    const [actual, setActual] = useState<{ canal: Canal; version: string } | null>(null);
    const [asistente, setAsistente] = useState<string | null>(null);
    const [texto, setTexto] = useState((canal.allowed_origins ?? []).join('\n'));
    const [intentado, setIntentado] = useState(false);

    useEffect(() => {
        let vivo = true;
        ops.getChannel(negocio, canal.id)
            .then((r) => {
                if (vivo) {
                    setActual({ canal: r.datos, version: r.version ?? '' });
                }
            })
            .catch(() => undefined);
        if (verAsistente) {
            ops.getAssistant(negocio)
                .then((r) => {
                    if (vivo) {
                        setAsistente(r.datos.status);
                    }
                })
                .catch(() => undefined);
        }
        return () => {
            vivo = false;
        };
    }, [ops, negocio, canal.id, verAsistente]);

    const c = actual?.canal ?? canal;
    const editable = gestiona && !soloLectura;
    const origenes = origenesDe(texto);
    const guardados = c.allowed_origins ?? [];
    const sinCambios = origenes !== null && origenes.length === guardados.length && origenes.every((o, i) => o === guardados[i]);
    const estadoAsistente: [string, EstadoChip] | null = asistente === null ? null : (ESTADO_ASISTENTE[asistente] ?? null);

    const guardar = () => {
        if (origenes === null) {
            setIntentado(true);
            return;
        }
        if (actual === null || sinCambios) {
            return;
        }
        const version = actual.version;
        const lista = origenes;
        retraso.programar({
            texto: 'Dominios del chat guardados.',
            donde: 'chat-web',
            enviar: () => {
                ops.updateChannel(negocio, c.id, { allowed_origins: lista }, version)
                    .then((r) => {
                        setActual({ canal: r.datos, version: r.version ?? '' });
                        recargarDatos();
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: e instanceof ErrorApi && e.codigo === 'version_conflict' ? 'Otra persona ha cambiado el canal. Ciérralo y ábrelo otra vez.' : (avisoBreveDeError(e) ?? 'No se ha podido guardar.') });
                    });
            },
            alDeshacer: () => { setTexto(guardados.join('\n')); },
        });
    };

    return (
        <PanelLateral
            abierto
            onCerrar={onCerrar}
            movil={movil}
            tamano="ficha"
            titulo={nombreDe(c)}
            eyebrow={TIPO.web_chat?.nombre ?? 'Chat de la web'}
            subtitulo={LINEA_CHAT_WEB}
            icono="globe"
            accion={editable ? 'Guardar' : ''}
            inactiva={sinCambios || actual === null}
            onAccion={guardar}
        >
            <div className="rv-cn-ficha">
                <div className="rv-cn-hechos">
                    <div className="rv-cn-hecho">
                        <span>Dominios permitidos</span>
                        <strong>{dominiosDelChat(c)}</strong>
                        <small>Desde ellos se puede abrir el chat</small>
                    </div>
                    <div className="rv-cn-hecho">
                        <span>Última conversación</span>
                        <strong>{c.last_conversation_at == null ? 'Todavía ninguna' : diaYHora(c.last_conversation_at)}</strong>
                    </div>
                    {estadoAsistente !== null && (
                        <div className="rv-cn-hecho">
                            <span>Asistente</span>
                            {estadoAsistente[1] === 'confirmed' ? <strong>{estadoAsistente[0]}</strong> : <span><Chip estado={estadoAsistente[1]}>{estadoAsistente[0]}</Chip></span>}
                            <small>Quien contesta en el chat</small>
                        </div>
                    )}
                </div>
                {retraso.pendiente !== null && <Deshacer texto={retraso.pendiente.texto} clave={retraso.pendiente.clave} fondo="suave" movil={movil} onDeshacer={() => { retraso.deshacer(); }} />}
                {gestiona && (
                    <Campo
                        etiqueta="Dominios permitidos"
                        valor={texto}
                        filas={3}
                        bloqueado={soloLectura}
                        error={intentado && origenes === null ? 'Algún dominio no es válido. Escribe uno por línea, por ejemplo casetaembrujo.es' : null}
                        ayuda={AYUDA_DOMINIOS_CHAT}
                        onChange={(v) => { setTexto(v); setIntentado(false); }}
                        nombre="chat-dominios"
                    />
                )}
                {soloLectura && gestiona && (
                    <p className="rv-cn-nota-gris" role="note">
                        <Icono nombre="eye" />
                        <span>El negocio está en solo lectura: no se puede cambiar el canal.</span>
                    </p>
                )}
            </div>
        </PanelLateral>
    );
}
